import { useReducer, useState } from 'react';

import { useMozartDialogsLang } from './useMozartDialogsLang';
import { useGetAllNotifications } from 'features/mozart-notifications/infra';

import type { MouseEvent } from 'react';
import type { SideBarDialogProps } from 'shared/components';
import type { MozartComplaint } from 'features/mozart-notifications/domain';

const DATA_ACTION_TYPE_KEY = 'data-action-type';
const DATA_ACTION_TYPE_VALUE = {
  primary: 'primary',
  secondary: 'secondary',
} as const;

const OPEN_DIALOG_INIT = {
  complain: false,
  turnOff: false,
  solved: false,
  turnOn: false,
};

export function useMozartDialogs() {
  const [complaint, setComplaint] = useState<MozartComplaint | null>(null);
  const [openDialog, setOpenDialog] = useReducer(funReducer, OPEN_DIALOG_INIT);

  const {
    isPlaceholderData: isLoading,
    data: mozartNotifications,
  } = useGetAllNotifications();

  const lang = useMozartDialogsLang();

  const notSeen = !complaint?.seen;
  const complaintDialogActions: SideBarDialogProps['actions'] = {
    primaryBtn: {
      // @ts-ignore
      [DATA_ACTION_TYPE_KEY]: DATA_ACTION_TYPE_VALUE.primary,
      children: notSeen ? lang.action.primary[complaint?.reason ?? ''] : '',
      onClick: onClickComplaintAction,
    },
    secondaryBtn: {
      // @ts-ignore
      [DATA_ACTION_TYPE_KEY]: DATA_ACTION_TYPE_VALUE.secondary,
      children: notSeen ? lang.action.secondary[complaint?.reason ?? ''] : '',
      onClick: onClickComplaintAction,
    },
  };

  const onSelectComplaint = (c: MozartComplaint) => {
    setComplaint(c);
    setOpenDialog({ type: 'complain' });
  };

  const onBackFromComplaintDialog = () => {
    setComplaint(null);
    setOpenDialog({ type: 'cleanState' });
  };

  const onBackToComplaintDialog = () => setOpenDialog({ type: 'complain' });

  function onClickComplaintAction(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (!complaint) return; // avoid using the null assertion
    const actionType = event.currentTarget.getAttribute(
      DATA_ACTION_TYPE_KEY,
    ) as DataActionType;
    const isPrimaryAction = actionType === DATA_ACTION_TYPE_VALUE.primary;
    const isWarning = complaint.reason === 'DEFECT_WARNING';
    if (isWarning && isPrimaryAction) setOpenDialog({ type: 'turnOff' });
    else if (isWarning && !isPrimaryAction) setOpenDialog({ type: 'solved' });
    else setOpenDialog({ type: 'turnOn' });
  }

  return {
    isLoading,
    openDialog,
    novelties: mozartNotifications!.novelties,
    complaints: mozartNotifications!.complaints,
    complaint,
    complaintDialogActions,
    onSelectComplaint,
    onBackFromComplaintDialog,
    onBackToComplaintDialog,
  };
}

type DataActionType = typeof DATA_ACTION_TYPE_VALUE[keyof typeof DATA_ACTION_TYPE_VALUE];

type StateOpenDialog = typeof OPEN_DIALOG_INIT;

type ActionOpenDialog = { type: keyof StateOpenDialog | 'cleanState' };

function funReducer(_: StateOpenDialog, action: ActionOpenDialog) {
  let result: StateOpenDialog = { ...OPEN_DIALOG_INIT };
  if (action.type === 'complain') result.complain = true;
  else if (action.type === 'turnOff') result.turnOff = true;
  else if (action.type === 'solved') result.solved = true;
  else if (action.type === 'turnOn') result.turnOn = true;
  return result;
}
